import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Clock, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { blogPosts } from '../mockData';
import SEOHead from '../components/SEOHead';

// How many "Latest Articles" cards to show before the reader has to click
// "Load More". Keeps the initial page weight low and turns a 43-post scroll
// into a deliberate browse.
const POSTS_PAGE_SIZE = 9;

// Preferred display order for category chips. Categories not listed here are
// appended alphabetically after these so nothing gets orphaned when new
// categories are introduced via mockData.js.
const PREFERRED_CATEGORY_ORDER = [
  'Brand Strategy',
  'AI-Powered Growth',
  'Creative Execution',
  'Retail & Last-Mile Experience',
  'Go-To-Market',
  'E-Commerce',
  'Digital Product Design',
];

// URL-safe kebab-case slug from a human-readable category name.
const catToSlug = (c) =>
  c.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') || 'all';

  // Sort all posts once (newest first) so both the chip counts and the grid
  // ordering agree on the source of truth.
  const sortedPosts = useMemo(
    () => [...blogPosts].sort((a, b) => new Date(b.date) - new Date(a.date)),
    []
  );

  // Build the chip list dynamically. Present the user-preferred taxonomy first
  // (in the exact order requested), then append any additional categories that
  // still exist in the data so no post is invisible.
  const categoryChips = useMemo(() => {
    const counts = sortedPosts.reduce((acc, p) => {
      acc[p.category] = (acc[p.category] || 0) + 1;
      return acc;
    }, {});
    const preferred = PREFERRED_CATEGORY_ORDER.filter((c) => counts[c] > 0);
    const extras = Object.keys(counts)
      .filter((c) => !PREFERRED_CATEGORY_ORDER.includes(c))
      .sort();
    return [
      { label: 'All', slug: 'all', count: sortedPosts.length },
      ...preferred.map((c) => ({ label: c, slug: catToSlug(c), count: counts[c] })),
      ...extras.map((c) => ({ label: c, slug: catToSlug(c), count: counts[c] })),
    ];
  }, [sortedPosts]);

  // Filter to the active category. If the URL param does not match any known
  // category slug, silently fall back to "all" rather than showing a blank
  // page for a stale link.
  const filteredPosts = useMemo(() => {
    if (activeCategory === 'all') return sortedPosts;
    const match = sortedPosts.filter((p) => catToSlug(p.category) === activeCategory);
    return match.length ? match : sortedPosts;
  }, [activeCategory, sortedPosts]);

  // Guard against arriving with a filter that has zero posts (deleted category).
  useEffect(() => {
    if (activeCategory === 'all') return;
    const known = categoryChips.some((c) => c.slug === activeCategory);
    if (!known) {
      const next = new URLSearchParams(searchParams);
      next.delete('category');
      setSearchParams(next, { replace: true });
    }
  }, [activeCategory, categoryChips, searchParams, setSearchParams]);

  const featuredPost = filteredPosts[0];
  const otherPosts = filteredPosts.slice(1);
  const activeLabel =
    categoryChips.find((c) => c.slug === activeCategory)?.label || 'All';

  // Progressive disclosure for the "Latest Articles" grid. Reset visible count
  // whenever the active filter changes so a category switch never lands you
  // on page 3 of the previous filter's overflow.
  const [visibleCount, setVisibleCount] = useState(POSTS_PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(POSTS_PAGE_SIZE);
  }, [activeCategory]);
  const visiblePosts = otherPosts.slice(0, visibleCount);
  const remainingCount = Math.max(0, otherPosts.length - visibleCount);

  const handleChipClick = (slug) => {
    const next = new URLSearchParams(searchParams);
    if (slug === 'all') {
      next.delete('category');
    } else {
      next.set('category', slug);
    }
    setSearchParams(next);
  };

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'RADIUS CONSULTING Blog',
    description:
      'Strategy, creativity, and the future of brand marketing. Insights on branding, design, AI marketing, and retail experience.',
    url: 'https://radiusconsulting.co.za/blog',
    publisher: {
      '@type': 'Organization',
      name: 'RADIUS CONSULTING',
      logo: {
        '@type': 'ImageObject',
        url: 'https://radiusconsulting.co.za/logo.png',
      },
    },
  };

  return (
    <>
      <SEOHead
        title="Blog - Insights & Perspectives"
        description="Expert insights on brand strategy, marketing with AI, retail experience design, and creative excellence. Learn from the RADIUS CONSULTING team."
        keywords="brand strategy, marketing AI, retail experience design, branding insights, creative marketing, business strategy"
        schema={schema}
      />
      <div className="blog-page pt-20">
        {/* Hero */}
        <section className="blog-hero py-24 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-30" />
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-6xl font-bold text-white mb-6">
                Insights &amp; Perspectives:<br />
                <span className="text-gradient">The RADIUS Blog</span>
              </h1>
              <p className="text-xl text-gray-400 leading-relaxed">
                Strategy, creativity, and the future of brand marketing.
              </p>
            </div>
          </div>
        </section>

        {/* Category filter chips */}
        <section
          className="category-filter py-3 md:py-8 bg-gray-900 border-b border-gray-800 sticky top-20 z-30 backdrop-blur-md"
          data-testid="blog-category-filter"
        >
          <div className="max-w-7xl mx-auto px-6">
            {/* Mobile: horizontal scroll row so chips don't eat the viewport.
                Desktop: original wrap layout. */}
            <div
              className="md:hidden flex gap-2 overflow-x-auto flex-nowrap items-center pb-1 -mx-2 px-2 blog-chip-scroll"
              data-testid="blog-category-scroll-mobile"
            >
              {categoryChips.map((chip) => {
                const isActive = chip.slug === activeCategory;
                return (
                  <button
                    key={chip.slug}
                    type="button"
                    onClick={() => handleChipClick(chip.slug)}
                    data-testid={`category-chip-${chip.slug}`}
                    aria-pressed={isActive}
                    className={
                      'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border whitespace-nowrap flex-shrink-0 ' +
                      (isActive
                        ? 'bg-red-600 border-red-600 text-white'
                        : 'bg-gray-950/60 border-gray-800 text-gray-300 hover:border-red-600/60 hover:text-white')
                    }
                  >
                    <span>{chip.label}</span>
                    <span
                      className={
                        'text-[10px] px-1 py-0.5 rounded-full ' +
                        (isActive ? 'bg-white/20 text-white' : 'bg-gray-800 text-gray-400')
                      }
                    >
                      {chip.count}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="hidden md:flex flex-wrap gap-3 items-center">
              {categoryChips.map((chip) => {
                const isActive = chip.slug === activeCategory;
                return (
                  <button
                    key={chip.slug}
                    type="button"
                    onClick={() => handleChipClick(chip.slug)}
                    data-testid={`category-chip-${chip.slug}-desktop`}
                    aria-pressed={isActive}
                    className={
                      'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all border ' +
                      (isActive
                        ? 'bg-red-600 border-red-600 text-white shadow-lg shadow-red-600/20'
                        : 'bg-gray-950/60 border-gray-800 text-gray-300 hover:border-red-600/60 hover:text-white')
                    }
                  >
                    <span>{chip.label}</span>
                    <span
                      className={
                        'text-xs px-1.5 py-0.5 rounded-full ' +
                        (isActive ? 'bg-white/20 text-white' : 'bg-gray-800 text-gray-400')
                      }
                    >
                      {chip.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Featured Post */}
        {featuredPost && (
          <section className="featured-post py-24 bg-gray-900">
            <div className="max-w-7xl mx-auto px-6">
              <div className="inline-block px-4 py-2 bg-red-600/10 border border-red-600/20 rounded-full mb-8">
                <span className="text-red-500 font-medium text-sm">
                  {activeCategory === 'all' ? 'Featured Article' : `Latest in ${activeLabel}`}
                </span>
              </div>

              <Card className="featured-post-card overflow-hidden hover-lift">
                <div className="grid lg:grid-cols-2">
                  <div className="relative h-96 lg:h-auto overflow-hidden bg-gray-950 flex items-center justify-center">
                    <img
                      src={featuredPost.image}
                      alt={featuredPost.title}
                      className="w-full h-full object-contain p-8"
                    />
                    <div className="absolute top-6 left-6">
                      <span className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-full">
                        {featuredPost.category}
                      </span>
                    </div>
                  </div>

                  <CardContent className="p-12 flex flex-col justify-center">
                    <h2 className="text-4xl font-bold text-white mb-4 hover:text-gradient transition-all">
                      {featuredPost.title}
                    </h2>
                    <p className="text-gray-400 text-lg mb-6 leading-relaxed">
                      {featuredPost.excerpt}
                    </p>

                    <div className="flex items-center gap-6 text-sm text-gray-500 mb-8">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4" />
                        <span>{featuredPost.author}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{featuredPost.readTime}</span>
                      </div>
                      <span>{new Date(featuredPost.date).toLocaleDateString()}</span>
                    </div>

                    <Link to={`/blog/${featuredPost.slug}`}>
                      <Button className="btn-primary group w-fit">
                        Read Full Article
                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardContent>
                </div>
              </Card>
            </div>
          </section>
        )}

        {/* Grid */}
        <section className="all-posts py-24 bg-black">
          <div className="max-w-7xl mx-auto px-6">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
              <h2 className="text-4xl font-bold text-white">
                {activeCategory === 'all' ? 'Latest Articles' : `More in ${activeLabel}`}
              </h2>
              {activeCategory !== 'all' && (
                <button
                  type="button"
                  onClick={() => handleChipClick('all')}
                  className="text-red-500 font-medium text-sm hover:text-red-400 transition-colors"
                  data-testid="clear-category-filter"
                >
                  Clear filter →
                </button>
              )}
            </div>

            {otherPosts.length === 0 ? (
              <p className="text-gray-400" data-testid="no-more-posts-in-category">
                No further posts in this category yet. Check back soon.
              </p>
            ) : (
              <>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
                  {visiblePosts.map((post) => (
                    <Link key={post.id} to={`/blog/${post.slug}`}>
                      <Card className="blog-card group overflow-hidden hover-lift h-full">
                        <div className="relative h-56 overflow-hidden">
                          <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                          <div className="absolute top-4 left-4">
                            <span className="px-3 py-1 bg-red-600/90 text-white text-xs font-medium rounded-full">
                              {post.category}
                            </span>
                          </div>
                        </div>

                        <CardContent className="p-6">
                          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-gradient transition-all line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                            {post.excerpt}
                          </p>

                          <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.readTime}
                            </span>
                            <span>{new Date(post.date).toLocaleDateString()}</span>
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="text-sm text-gray-400">{post.author}</div>
                            <ArrowRight className="w-5 h-5 text-red-500 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>

                {remainingCount > 0 && (
                  <div className="flex flex-col items-center mt-16 gap-3">
                    <Button
                      type="button"
                      size="lg"
                      onClick={() =>
                        setVisibleCount((c) => c + POSTS_PAGE_SIZE)
                      }
                      className="btn-primary group"
                      data-testid="load-more-blogs-btn"
                    >
                      Load More Articles
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                    <span
                      className="text-xs text-gray-500"
                      data-testid="load-more-remaining-count"
                    >
                      Showing {visiblePosts.length} of {otherPosts.length}
                      {remainingCount === 1
                        ? ' · 1 article remaining'
                        : ` · ${remainingCount} articles remaining`}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Blog;
